/**
 * Central image optimization layer.
 *
 * Every remote image (Supabase storage, Wikimedia, Unsplash, any https host)
 * is routed through the weserv.nl image CDN, which:
 *   - re-encodes to modern WebP on the fly (`output=webp`)
 *   - resizes to the requested width (responsive `srcset`)
 *   - caches globally, so rate-limited hosts (Wikimedia 429s) never break
 *
 * Site-relative paths (`/img/...`, `/api/public/box-image/...`, bundled
 * assets) are left untouched: they are already served from our own CDN with
 * immutable caching, and proxying them would break local/preview builds.
 *
 * Use `SafeImage` (which calls these helpers automatically) rather than a raw
 * <img> so every image gets WebP + lazy loading + responsive sizing for free.
 */

/** Local, always-available fallback used when a remote image fails to load. */
export const IMAGE_FALLBACK = "/og-default.jpg";

/** Widths generated for responsive `srcset` (device-pixel friendly). */
export const RESPONSIVE_WIDTHS = [320, 480, 640, 828, 1080, 1280, 1600, 1920];

/** Hosts we must never proxy (already optimized, or not publicly fetchable). */
const NEVER_PROXY = new Set([
  "images.weserv.nl",
  // flagcdn already serves pre-sized WebP flags — an extra hop would slow them.
  "flagcdn.com",
  "localhost",
  "127.0.0.1",
]);

function isRemote(src: string): boolean {
  return /^https?:\/\//i.test(src);
}

function hostOf(src: string): string | null {
  try {
    return new URL(src).host;
  } catch {
    return null;
  }
}

/**
 * Site-relative paths that hold uploaded (non-WebP) originals. These are made
 * absolute against the production domain so the CDN can fetch and convert
 * them. In dev/preview the CDN copy may not exist yet — `SafeImage` falls back
 * to the original path automatically.
 */
const ABSOLUTIZE_PREFIXES = ["/api/public/box-image/"];

function absolutize(src: string): string {
  if (isRemote(src)) return src;
  if (!ABSOLUTIZE_PREFIXES.some((p) => src.startsWith(p))) return src;
  return `https://lifewestway.com${src}`;
}

/** True when the URL can be optimized by the image CDN. */
export function isOptimizable(src?: string): src is string {
  if (!src) return false;
  const abs = absolutize(src);
  if (!isRemote(abs)) return false;
  const host = hostOf(abs);
  return !!host && !NEVER_PROXY.has(host) && !host.startsWith("localhost");
}

/**
 * Returns a WebP-encoded, optionally resized URL for any remote image, and the
 * original URL for local/relative assets and data URIs.
 */
export function optimizedImageUrl(src?: string, width?: number, quality = 78): string | undefined {
  if (!src) return src;
  if (!isOptimizable(src)) return src;

  const base = absolutize(src).replace(/^https?:\/\//i, "");
  const params = new URLSearchParams({ url: base, output: "webp", q: String(quality) });
  if (width) {
    params.set("w", String(width));
    params.set("fit", "cover");
    params.set("we", "1");
  }
  return `https://images.weserv.nl/?${params.toString()}`;
}

/** Back-compat alias used across the codebase. */
export const resilientImageUrl = optimizedImageUrl;

/**
 * Builds a responsive `srcset` for a remote image, capped at the intrinsic
 * width so we never upscale. Returns undefined for local/relative assets.
 */
export function buildSrcSet(src?: string, intrinsicWidth?: number): string | undefined {
  if (!isOptimizable(src)) return undefined;
  const max = intrinsicWidth && intrinsicWidth > 0 ? intrinsicWidth * 2 : 1920;
  const widths = RESPONSIVE_WIDTHS.filter((w) => w <= max);
  if (widths.length === 0) widths.push(Math.min(max, 1920));
  return widths.map((w) => `${optimizedImageUrl(src, w)} ${w}w`).join(", ");
}

/** Sensible default `sizes` when the caller doesn't supply one. */
export function defaultSizes(intrinsicWidth?: number): string {
  if (intrinsicWidth && intrinsicWidth <= 96) return `${intrinsicWidth}px`;
  if (intrinsicWidth && intrinsicWidth <= 480) return `(max-width: 640px) 50vw, ${intrinsicWidth}px`;
  return "(max-width: 640px) 100vw, (max-width: 1024px) 75vw, 1200px";
}
